import { Injectable, signal, Signal } from '@angular/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { isCapacitorNative } from 'environments/environment-helpers';
import { LL } from 'i18n/i18n.service';
import { SettingsService } from './settings.service';
import { Feedback, triggerFeedback } from './haptics.service';
import { APP_TITLE } from './notification-builder';
import { WakeLockService } from './wake-lock.service';
import { LoggingService } from './logging.service';
import { errorReportFields } from './error-patterns';
import {
  clampRestSeconds,
  REST_TIMER_MAX_SECONDS,
  REST_TIMER_MIN_SECONDS,
  REST_TIMER_STEP_SECONDS,
} from '@cf-shared/data-model/rest-timer';
import {
  clearRestTimerNotificationAsync,
  REST_TIMER_NOTIFICATION_ID,
} from './rest-timer-notifications';

const UI_TICK_MS = 1000;
// how long the "Done!" flash stays on screen after the user acknowledges
// the expiry (first pointerdown / keydown / scroll), before the service
// auto-dismisses itself and the pill disappears
const DONE_FLASH_MS = 1500;
// document-level events that count as user acknowledgment of timer expiry —
// see attachExpiryAcknowledgeListeners. listed once so attach and detach
// stay in lockstep.
const EXPIRY_ACK_EVENTS = ['pointerdown', 'keydown', 'scroll'] as const;
// addEventListener / removeEventListener must receive the same `capture`
// flag for removal to find the listener. `passive` is harmless on remove.
const EXPIRY_ACK_LISTENER_OPTS: AddEventListenerOptions = { capture: true, passive: true };

@Injectable({ providedIn: 'root' })
export class RestTimerService {
  // stable wake-lock tag for this service: the rest-timer is a singleton with
  // only one logical timer at a time (cancelInternalAsync enforces this), so
  // a fixed tag is sufficient. WakeLockService's refcount + race
  // reconciliation handles cancel-during-in-flight-acquire.
  private static readonly WAKE_LOCK_TAG = 'rest-timer';

  // signals exposed to UI; null / 0 / false when no timer is running
  private readonly _remainingSeconds = signal<number | null>(null);
  private readonly _isRunning = signal<boolean>(false);
  private readonly _exerciseName = signal<string | null>(null);
  private readonly _isExpired = signal<boolean>(false);
  private readonly _totalSeconds = signal<number | null>(null);

  readonly remainingSeconds: Signal<number | null> = this._remainingSeconds.asReadonly();
  // the length of the running rest: the started duration plus every ±15 s adjust, so
  // remaining / total is the share still to go (the workout page's countdown fill, #6575)
  readonly totalSeconds: Signal<number | null> = this._totalSeconds.asReadonly();
  readonly isRunning: Signal<boolean> = this._isRunning.asReadonly();
  readonly exerciseName: Signal<string | null> = this._exerciseName.asReadonly();
  // true while the "Done!" flash is showing (between expiry and auto-dismiss)
  readonly isExpired: Signal<boolean> = this._isExpired.asReadonly();

  // internal state
  private targetEndTimeMs: number | null = null;
  // log _id of the workout this timer belongs to, so the scheduled OS notification
  // can deep-link straight back to the active workout page when tapped (#4176).
  // held across adjust() reschedules; cleared on cancel.
  private workoutId: string | null = null;
  private tickHandle: ReturnType<typeof setInterval> | null = null;
  private doneFlashHandle: ReturnType<typeof setTimeout> | null = null;
  // stored bound reference so detach can hand `removeEventListener` the
  // same function on the cancel / next-set paths (where no event has fired
  // yet, so `{ once: true }` self-removal wouldn't have run).
  private readonly onUserAcknowledgeExpiry = () => this.handleExpiryAcknowledged();

  constructor(
    private readonly settings: SettingsService,
    private readonly wakeLock: WakeLockService,
    private readonly logging: LoggingService,
  ) {}

  // whether the rest-timer runtime is allowed on this platform. native is
  // always allowed; PWA/desktop only when the test-mode debug override is on.
  // pill/expanded components read this to decide whether to render.
  isAllowedOnThisPlatform(): boolean {
    if (isCapacitorNative()) return true;
    return this.settings.debugRestTimerOnWeb === true;
  }
  // monotonic generation token: every start/adjust/cancel bumps it so that
  // in-flight async OS-plugin calls from a superseded generation can detect
  // they are stale and bail before committing side-effects
  private generation = 0;

  // start a new rest countdown; cancels any running timer first. only runs
  // when isAllowedOnThisPlatform() — native always, PWA/desktop only when
  // the test-mode debugRestTimerOnWeb flag is on.
  async start(seconds: number, context: { exerciseName: string; workoutId: string }): Promise<void> {
    if (!this.isAllowedOnThisPlatform()) {
      console.log('RestTimerService.start: skipped (non-native platform, debug flag off)');
      return;
    }
    await this.cancelInternalAsync({ silent: true });

    const gen = ++this.generation;
    const clamped = clampRestSeconds(seconds);
    this.targetEndTimeMs = Date.now() + clamped * 1000;
    this.workoutId = context.workoutId;
    this._exerciseName.set(context.exerciseName);
    this._remainingSeconds.set(clamped);
    this._totalSeconds.set(clamped);
    this._isRunning.set(true);
    this._isExpired.set(false);
    // start ticking immediately so the pill begins counting down; the wake
    // lock and OS notification are scheduled in parallel (best-effort)
    this.startTickInterval();
    await Promise.all([
      this.wakeLock.acquireAsync(RestTimerService.WAKE_LOCK_TAG),
      this.scheduleNotificationAsync(gen, context.exerciseName, context.workoutId),
    ]);
  }

  // adjust the running timer by ±15s. clamps the NEW remaining seconds into
  // [REST_TIMER_MIN_SECONDS, REST_TIMER_MAX_SECONDS]. rapid successive calls
  // are serialized via the generation token so only the latest cancel+
  // schedule pair commits to the OS.
  async adjust(deltaSeconds: number): Promise<void> {
    if (!this._isRunning() || this.targetEndTimeMs == null) return;
    const currentRemaining = Math.max(0, Math.round((this.targetEndTimeMs - Date.now()) / 1000));
    // snap delta to step; clamp new remaining into range
    const step = Math.round(deltaSeconds / REST_TIMER_STEP_SECONDS) * REST_TIMER_STEP_SECONDS;
    let next = currentRemaining + step;
    if (next < REST_TIMER_MIN_SECONDS) next = REST_TIMER_MIN_SECONDS;
    if (next > REST_TIMER_MAX_SECONDS) next = REST_TIMER_MAX_SECONDS;

    const gen = ++this.generation;
    this.targetEndTimeMs = Date.now() + next * 1000;
    this._remainingSeconds.set(next);
    // the rest grows or shrinks by what the clamp actually let through
    this._totalSeconds.update(total => (total ?? currentRemaining) + (next - currentRemaining));
    // reschedule OS notification so it fires at the new end time; if a later
    // adjust bumps the generation while we're mid-flight, both calls below
    // will bail before mutating the OS state
    await this.cancelNotificationAsync(gen);
    await this.scheduleNotificationAsync(gen, this._exerciseName() ?? '', this.workoutId ?? undefined);
  }

  // user-initiated dismissal: cancel timer, cancel notification, release
  // wake-lock, fire a light haptic tap to acknowledge.
  async skip(): Promise<void> {
    if (!this._isRunning()) return;
    await this.cancelInternalAsync({ silent: false });
  }

  // test-only: collapse the remaining time to zero on the next tick so E2E
  // can exercise the expiry path without waiting REST_TIMER_MIN_SECONDS.
  testForceExpireSoon(): void {
    if (!this._isRunning()) return;
    this.targetEndTimeMs = Date.now();
  }

  // silent cancellation (set reopen / page leave). no haptic.
  async cancel(): Promise<void> {
    if (!this._isRunning()) return;
    await this.cancelInternalAsync({ silent: true });
  }

  private async cancelInternalAsync(opts: { silent: boolean }): Promise<void> {
    const gen = ++this.generation;
    this.stopTickInterval();
    this.clearDoneFlash();
    this.detachExpiryAcknowledgeListeners();
    await this.cancelNotificationAsync(gen);
    await this.wakeLock.releaseAsync(RestTimerService.WAKE_LOCK_TAG);
    this.targetEndTimeMs = null;
    this.workoutId = null;
    this._remainingSeconds.set(null);
    this._totalSeconds.set(null);
    this._exerciseName.set(null);
    this._isRunning.set(false);
    this._isExpired.set(false);
    if (!opts.silent) {
      triggerFeedback(Feedback.ImpactLight);
    }
  }

  private clearDoneFlash(): void {
    if (this.doneFlashHandle != null) {
      clearTimeout(this.doneFlashHandle);
      this.doneFlashHandle = null;
    }
  }

  // attach document-level listeners on first expiry; first event treated as
  // user acknowledgment (phone is out, eyes are on the screen). capture
  // phase so child handlers stopping propagation don't hide acknowledgment.
  private attachExpiryAcknowledgeListeners(): void {
    for (const evt of EXPIRY_ACK_EVENTS) {
      document.addEventListener(evt, this.onUserAcknowledgeExpiry, EXPIRY_ACK_LISTENER_OPTS);
    }
  }

  private detachExpiryAcknowledgeListeners(): void {
    for (const evt of EXPIRY_ACK_EVENTS) {
      document.removeEventListener(evt, this.onUserAcknowledgeExpiry, EXPIRY_ACK_LISTENER_OPTS);
    }
  }

  private handleExpiryAcknowledged(): void {
    // ignore stray events after manual cancel / next-set start has already
    // cleared the expired state.
    if (!this._isExpired()) return;
    this.detachExpiryAcknowledgeListeners();
    this.clearDoneFlash();
    this.doneFlashHandle = setTimeout(() => {
      this.doneFlashHandle = null;
      if (this._isExpired()) {
        void this.cancelInternalAsync({ silent: true });
      }
    }, DONE_FLASH_MS);
  }

  // drive the signals from the wall-clock end time once per second; when
  // we hit 0, fire a haptic buzz, release the wake-lock, show the "Done!"
  // flash, then auto-dismiss. the OS notification handles the audible alert
  // in foreground & background.
  private startTickInterval(): void {
    this.stopTickInterval();
    this.tickHandle = setInterval(() => this.tick(), UI_TICK_MS);
  }

  private stopTickInterval(): void {
    if (this.tickHandle != null) {
      clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
  }

  private tick(): void {
    if (this.targetEndTimeMs == null) {
      this.stopTickInterval();
      return;
    }
    const remaining = Math.max(0, Math.round((this.targetEndTimeMs - Date.now()) / 1000));
    this._remainingSeconds.set(remaining);
    if (remaining <= 0) {
      this.stopTickInterval();
      triggerFeedback(Feedback.Buzz);
      // release the wake-lock at expiry — the "Done!" pill no longer needs
      // the screen pinned awake. cancelInternalAsync (run after the user
      // acknowledgment auto-dismiss) will also call releaseAsync on the same
      // tag; the second call is an intended no-op via WakeLockService's
      // delete-returns-false idempotency.
      void this.wakeLock.releaseAsync(RestTimerService.WAKE_LOCK_TAG);
      this._isExpired.set(true);
      this.clearDoneFlash();
      // hold the "Done!" pill indefinitely until the user touches the screen;
      // auto-dismiss starts only after that acknowledgment so a phone-in-pocket
      // user still sees the result when they look. see GitHub #3221.
      this.attachExpiryAcknowledgeListeners();
    }
  }

  private async scheduleNotificationAsync(gen: number, exerciseName: string, workoutId: string | undefined): Promise<void> {
    if (this.targetEndTimeMs == null) return;
    // on non-native (debug-on-web), the LocalNotifications plugin is a web
    // no-op / may throw; skip entirely — the tick interval still drives the UI
    if (!isCapacitorNative()) return;
    try {
      // if permission not yet granted, ask once. mirrors NotificationService.
      const perm = await LocalNotifications.checkPermissions();
      if (perm.display === 'prompt' || perm.display === 'prompt-with-rationale') {
        const req = await LocalNotifications.requestPermissions();
        if (req.display !== 'granted') {
          console.log('RestTimerService: notification permission denied; timer will tick but no OS alert');
          return;
        }
      } else if (perm.display !== 'granted') {
        console.log('RestTimerService: notification permission not granted; timer will tick but no OS alert');
        return;
      }
      // if a newer generation took over while we were awaiting permissions,
      // bail to avoid scheduling a stale notification
      if (gen !== this.generation || this.targetEndTimeMs == null) return;
      const t = LL.workouts.restTimer;
      await LocalNotifications.schedule({
        notifications: [{
          id: REST_TIMER_NOTIFICATION_ID,
          title: APP_TITLE,
          body: exerciseName ? `${t.notificationBody()} — ${exerciseName}` : t.notificationBody(),
          schedule: { at: new Date(this.targetEndTimeMs) },
          // iOS-required: without 'sound', Capacitor sets UNNotificationContent.sound = nil → silent.
          // Android resolves 'default' → fallback to channel default sound.
          sound: 'default',
          // deep-link straight to the active workout page (not the workouts LIST) so
          // tapping the alert resumes the workout with no extra "Resume" step (#4176).
          // notification.service.ts's tap handler reads page + workoutId.
          extra: { page: 'workout-detail', workoutId, stringId: 'rest-timer' },
        }],
      });
    } catch (err) {
      // a failed schedule means no OS alert will fire — the whole point of
      // the feature — so it must reach our error reports (#5191)
      this.logging.trackSilentProblem('RestTimerScheduleFailed', errorReportFields(err));
    }
  }

  private async cancelNotificationAsync(gen: number): Promise<void> {
    if (!isCapacitorNative()) return;
    try {
      // if a newer generation already scheduled, don't cancel its work
      if (gen !== this.generation) return;
      // clear the pending notification AND any already-delivered tray entry from
      // a prior set — see clearRestTimerNotificationAsync for why the delivered
      // sweep matters (#4084: the tray stayed stuck on the first exercise).
      await clearRestTimerNotificationAsync(LocalNotifications);
    } catch (err) {
      // a failed cancel leaves a stale "rest over" alert to fire later, or a stuck tray
      // entry from the previous set (the #4084 shape) (#5191)
      this.logging.trackSilentProblem('RestTimerCancelFailed', errorReportFields(err));
    }
  }

}
